import React, { useMemo, useState } from 'react'
import { ICampaignRequest } from '@dataTypes/marketing.type'
import {
  AccordionComponent,
  AccordionItemDirective,
  AccordionItemsDirective,
} from '@syncfusion/ej2-react-navigations'
import { format } from 'date-fns'
import { formatMilitaryTimeTo12Hour } from '@utils/time.util'
import { formatRecurringDays } from '@utils/date.utils'
import { TIMEZONE_OPTIONS } from '@constants/Common/date.constant'
import { MessagePreviewModal } from '../..'
import ViewRecipientsModal from '../../ViewRecipients/ViewRecipients.component'
import { useAppSelector } from '@store/hooks'

interface ReviewProps {
  campaignData: ICampaignRequest
  handleEdit: () => void
}

const Review: React.FC<ReviewProps> = ({ campaignData, handleEdit }) => {
  const [showMessagePreview, setShowMessagePreview] = useState(false)
  const [showSelectedRecipients, setShowSelectedRecipients] = useState(false)
  const campaignTemplates = useAppSelector(
    (state) => state.marketing.campaignTemplates,
  )
  const emailCanvas = useMemo(
    () =>
      campaignTemplates.find(
        (t) => t.campaign_template_id === campaignData.campaign_template_id,
      )?.email_editor_canvas,
    [campaignTemplates, campaignData.campaign_template_id],
  )

  const getTimePoints = (): JSX.Element => {
    const filteredTP = campaignData.campaign_trigger_message.filter(
      (tp) => tp.status === 'Active',
    )
    if (filteredTP.length > 0) {
      return (
        <>
          {filteredTP.map((tp) => (
            <span key={tp.campaign_trigger_message_id}>
              {`${tp.name} (${tp.send_on} ${tp.send_duration} ${tp.send_option.toLowerCase()})`}
            </span>
          ))}
        </>
      )
    }
    return <>-</>
  }

  const getActionTriggers = (): JSX.Element => {
    const filteredAT = campaignData.campaign_trigger_keyword.filter(
      (tk) => tk.status === 'Active',
    )
    if (filteredAT.length > 0) {
      return (
        <>
          {filteredAT.map((tk) => (
            <span
              key={tk.campaign_trigger_keyword_id}
            >{`${tk.name} (${tk.keyword})`}</span>
          ))}
        </>
      )
    }
    return <>-</>
  }

  return (
    <>
      <div className="ac-review">
        <div className="ac-input-label">
          <span className="ac-header-label">Review the campaign info</span>
          <button
            type="button"
            className="unstyled-button ac-edit-label"
            onClick={handleEdit}
          >
            <span className="mgc_pencil_fill" />
            Edit
          </button>
        </div>
        <AccordionComponent id="review-accordion-id" key="review-accordion-key">
          <AccordionItemsDirective>
            <AccordionItemDirective
              key="campaign-info-key"
              expanded={true}
              header="Campaign info"
              content={() => (
                <div id="campaign-info-content">
                  <div className="rev-item">
                    <span className="rev-item-label">Campaign name</span>
                    <span className="rev-item-value">{campaignData.name}</span>
                  </div>
                  <div className="rev-item">
                    <span className="rev-item-label">Campaign type</span>
                    <span className="rev-item-value">{campaignData.type}</span>
                  </div>
                  <div className="rev-item">
                    <span className="rev-item-label">Send via</span>
                    <span className="rev-item-value">
                      {campaignData.channel}
                    </span>
                  </div>
                  {campaignData.channel === 'Email' && (
                    <div className="rev-item">
                      <span className="rev-item-label">Subject</span>
                      <span className="rev-item-value">
                        {campaignData.subject || '-'}
                      </span>
                    </div>
                  )}
                  <div className="rev-item">
                    <span className="rev-item-label">Message</span>
                    <button
                      type="button"
                      className="unstyled-button rev-item-value-link"
                      onClick={() => setShowMessagePreview(true)}
                    >
                      Preview
                    </button>
                  </div>
                  <div className="rev-item">
                    <span className="rev-item-label">Selected recipients</span>
                    <button
                      type="button"
                      className="unstyled-button rev-item-value-link"
                      onClick={() => setShowSelectedRecipients(true)}
                    >
                      View
                    </button>
                  </div>
                  <div className="rev-item">
                    <span className="rev-item-label">Description</span>
                    <span className="rev-item-value">
                      {campaignData.description}
                    </span>
                  </div>
                </div>
              )}
            />
            <AccordionItemDirective
              key="scheduling-key"
              expanded={true}
              header="Scheduling"
              content={() => (
                <div id="scheduling-content">
                  <div className="rev-item">
                    <span className="rev-item-label">Message send option</span>
                    <span className="rev-item-value">
                      {campaignData.sending_option}
                      {campaignData.sending_option === 'Recurring'
                        ? ` (${campaignData.recurring_interval})`
                        : ''}
                    </span>
                  </div>
                  {campaignData.sending_option === 'Recurring' ? (
                    <div className="rev-item">
                      <span className="rev-item-label">Recurring days</span>
                      <span className="rev-item-value">
                        {formatRecurringDays(campaignData.recurring_days)}
                        {` (${formatMilitaryTimeTo12Hour(campaignData.recurring_time)} - ${campaignData.recurring_duration} ${campaignData.recurring_interval === 'Daily' ? 'days' : 'weeks'})`}
                      </span>
                    </div>
                  ) : campaignData.sending_option === 'Later' ? (
                    <div className="rev-item">
                      <span className="rev-item-label">Schedule date</span>
                      <span className="rev-item-value">
                        {campaignData.schedule_date &&
                          format(
                            new Date(campaignData.schedule_date),
                            'MM/dd/yyyy',
                          )}
                        {` - ${formatMilitaryTimeTo12Hour(campaignData.schedule_time)}`}
                      </span>
                    </div>
                  ) : (
                    <></>
                  )}
                  <div className="rev-item">
                    <span className="rev-item-label">Timezone</span>
                    <span className="rev-item-value">
                      {TIMEZONE_OPTIONS.find(
                        (option) => option.value === campaignData.timezone,
                      )?.label || campaignData.timezone}
                    </span>
                  </div>
                  <div className="rev-item">
                    <span className="rev-item-label">Period start</span>
                    <span className="rev-item-value">
                      {campaignData.start_date &&
                        format(new Date(campaignData.start_date), 'MM/dd/yyyy')}
                      {` - ${formatMilitaryTimeTo12Hour(campaignData.start_time)}`}
                    </span>
                  </div>
                  <div className="rev-item">
                    <span className="rev-item-label">Ends on</span>
                    <span className="rev-item-value">
                      {campaignData.end_date &&
                        format(new Date(campaignData.end_date), 'MM/dd/yyyy')}
                      {` - ${formatMilitaryTimeTo12Hour(campaignData.end_time)}`}
                    </span>
                  </div>
                </div>
              )}
            />
            <AccordionItemDirective
              key="trigger-events-key"
              expanded={true}
              header="Trigger events"
              content={() => (
                <div id="trigger-events-content">
                  <div className="rev-item">
                    <span className="rev-item-label">Time points</span>
                    <span className="rev-item-value">{getTimePoints()}</span>
                  </div>
                  <div className="rev-item">
                    <span className="rev-item-label">Action triggers</span>
                    <span className="rev-item-value">
                      {getActionTriggers()}
                    </span>
                  </div>
                </div>
              )}
            />
          </AccordionItemsDirective>
        </AccordionComponent>
      </div>
      {showMessagePreview && campaignData.html_content && (
        <MessagePreviewModal
          show={showMessagePreview}
          onHide={() => setShowMessagePreview(false)}
          content={campaignData.html_content}
          channel={campaignData.channel}
          subject={campaignData.subject}
          emailCanvas={emailCanvas}
        />
      )}
      {showSelectedRecipients && campaignData.campaign_message && (
        <ViewRecipientsModal
          recipients={campaignData.campaign_recipient}
          show={showSelectedRecipients}
          onHide={() => setShowSelectedRecipients(false)}
        />
      )}
    </>
  )
}

export default Review
